/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { List, Avatar, message } from 'antd';
import React, { useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  dbFileToTableEntry,
  dbFolderToTableEntry,
  TableEntry,
} from '~components/atoms/FileTables/FileTable.utils';
import { FileListItem } from '~components/atoms/mobile/FileListItem/FileListItem';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useProjectId } from '~src/hooks/useProjectId';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { downloadFileLastVersion } from '~src/utils/download';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { useIntl } from 'react-intl';
import { appLog } from '~src/utils/log';

export const FavouritesList: React.FC = () => {
  const styles = useStyles(styleFn);
  const history = useHistory();
  const projectId = useProjectId();
  const intl = useIntl();

  const { fileVersionsContext } = useContext(FileVersionsContext);

  const filesQuery = useFiles(
    { favourites: true, withVersions: true, oldFilesImplementation: true },
    {
      onError: () =>
        // go to /archive
        history.replace(`/projects/${projectId}/archive`),
    }
  );

  const foldersQuery = useFolders({
    favourites: true,
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  });

  const foldersSource = useMemo(
    () => (foldersQuery.data || []).map(dbFolderToTableEntry),
    [foldersQuery.data]
  );
  const filesSource = useMemo(
    () => (filesQuery.data || []).map((item) => dbFileToTableEntry(item)),
    [filesQuery.data]
  );

  const handleFolderClick = (record: TableEntry) => {
    // window.clearTimeout(clickTimeoutRef.current);
    // clickTimeoutRef.current = undefined;
    history.push(`/projects/${projectId}/archive/${record.key}`);
  };

  const handleFileClick = (record: TableEntry) => {
    if (record.dbFile) {
      downloadFileLastVersion(record.dbFile, fileVersionsContext, () =>
        message.info(intl.formatMessage({ id: 'download-starting' }))
      ).catch((err) => {
        appLog('downloadFile -> err', err);
        message.error(intl.formatMessage({ id: 'file-download-failed' }));
      });
    }
  };

  return (
    <div css={styles.container}>
      <List<TableEntry>
        dataSource={[...foldersSource, ...filesSource]}
        css={{ widt: '100%' }}
        loading={filesQuery.isLoading || foldersQuery.isLoading}
        renderItem={(item) => (
          <FileListItem
            item={item}
            onClick={
              item.folder
                ? () => handleFolderClick(item)
                : () => handleFileClick(item)
            }
          />
        )}
      />
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    height: '100%',
    width: '100%',
  },
});
